import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    InputAdornment,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Delete as DeleteIcon,
    History as HistoryIcon,
    OpenInNew as OpenInNewIcon,
    Search as SearchIcon,
    StopCircle as StopIcon,
} from '@mui/icons-material';
import {
    cancelReportMatchingJob,
    deleteReportMatchingJob,
    deleteMatchingResult,
    getWorkspace,
    getWorkspaceMatchings,
    listReportMatchingJobs,
} from '../services/api';

function parseApiDate(value) {
    if (!value) return null;
    const text = String(value).trim();
    if (!text) return null;
    const normalized = /(?:Z|[+-]\d{2}:\d{2})$/i.test(text) ? text : `${text}Z`;
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function ReportMatchingHistoryPage() {
    const { workspaceId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [workspace, setWorkspace] = useState(null);
    const [savedMatchings, setSavedMatchings] = useState([]);
    const [matchJobs, setMatchJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stoppingJobIds, setStoppingJobIds] = useState({});
    const [activeView, setActiveView] = useState('outputs');
    const [outputSearch, setOutputSearch] = useState('');

    useEffect(() => {
        const requestedView = String(searchParams.get('view') || '').trim().toLowerCase();
        if (requestedView === 'history' || requestedView === 'outputs') {
            setActiveView(requestedView);
            return;
        }
        setActiveView('outputs');
    }, [searchParams]);

    const refreshData = async () => {
        if (!workspaceId) {
            return;
        }
        try {
            setLoading(true);
            const [workspaceData, matchingsData, jobsData] = await Promise.all([
                getWorkspace(workspaceId),
                getWorkspaceMatchings(workspaceId),
                listReportMatchingJobs(workspaceId, { limit: 100 }),
            ]);
            setWorkspace(workspaceData || null);
            setSavedMatchings(matchingsData || []);
            setMatchJobs(jobsData || []);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to load workspace history.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refreshData();
    }, [workspaceId]);

    const isCancelableJob = (job) => ['queued', 'running'].includes(job?.status);
    const isDeletableJob = (job) => ['failed', 'interrupted', 'cancelled'].includes(job?.status);

    const getJobStatusColor = (status) => {
        if (status === 'completed') return 'success';
        if (status === 'failed' || status === 'interrupted') return 'error';
        if (status === 'cancelled') return 'warning';
        if (status === 'running') return 'info';
        return 'warning';
    };

    const getJobReason = (job) => (
        job?.status_detail
        || job?.error
        || job?.progress_message
        || 'Waiting for status...'
    );

    const summary = useMemo(() => {
        const result = {
            queued: 0,
            running: 0,
            completed: 0,
            failed: 0,
            outputs: savedMatchings.length,
        };
        (matchJobs || []).forEach((job) => {
            if (job.status === 'queued') result.queued += 1;
            else if (job.status === 'running') result.running += 1;
            else if (job.status === 'completed') result.completed += 1;
            else if (job.status === 'failed' || job.status === 'interrupted' || job.status === 'cancelled') result.failed += 1;
        });
        return result;
    }, [matchJobs, savedMatchings]);

    const filteredSavedMatchings = useMemo(() => {
        const query = outputSearch.trim().toLowerCase();
        if (!query) return savedMatchings;
        return (savedMatchings || []).filter((item) => (
            String(item?.legacy_report_name || '').toLowerCase().includes(query)
        ));
    }, [outputSearch, savedMatchings]);

    const processingTimeByReport = useMemo(() => {
        const result = {};
        (matchJobs || []).forEach((job) => {
            const reportName = String(job?.legacy_report_name || '').trim();
            if (!reportName) return;
            if (!job?.started_at || !job?.completed_at) return;
            const startedAt = parseApiDate(job.started_at);
            const completedAt = parseApiDate(job.completed_at);
            if (!startedAt || !completedAt) return;
            const durationMs = completedAt.getTime() - startedAt.getTime();
            if (!Number.isFinite(durationMs) || durationMs < 0) return;

            const existing = result[reportName];
            const completedAtMs = completedAt.getTime();
            if (!existing || completedAtMs > existing.completedAtMs) {
                result[reportName] = {
                    minutes: durationMs / 60000,
                    completedAtMs,
                };
            }
        });
        return result;
    }, [matchJobs]);

    const handleDeleteMatching = async (reportName) => {
        if (!workspaceId || !window.confirm(`Delete saved output for "${reportName}"?`)) {
            return;
        }
        try {
            await deleteMatchingResult(workspaceId, reportName);
            await refreshData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to delete saved output.');
        }
    };

    const handleCancelJob = async (job) => {
        if (!workspaceId || !job?.id || !isCancelableJob(job)) {
            return;
        }
        setStoppingJobIds((prev) => ({ ...prev, [job.id]: true }));
        try {
            const updatedJob = await cancelReportMatchingJob(workspaceId, job.id);
            setMatchJobs((prev) => prev.map((item) => (item.id === updatedJob.id ? updatedJob : item)));
            setError(null);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to stop matching job.');
        } finally {
            setStoppingJobIds((prev) => {
                const next = { ...prev };
                delete next[job.id];
                return next;
            });
        }
    };

    const handleDeleteJob = async (job) => {
        if (!workspaceId || !job?.id || !isDeletableJob(job)) {
            return;
        }
        if (!window.confirm(`Delete failed queue entry for "${job.legacy_report_name}"?`)) {
            return;
        }
        try {
            await deleteReportMatchingJob(workspaceId, job.id);
            setMatchJobs((prev) => prev.filter((item) => item.id !== job.id));
            setError(null);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to delete matching job.');
        }
    };

    const openSavedOutput = (reportName) => {
        const url = `/report-matching/workspaces/${workspaceId}?report=${encodeURIComponent(reportName)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleActiveViewChange = (_event, value) => {
        if (!value) return;
        setActiveView(value);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('view', value);
            return next;
        });
    };

    const formatProcessingMinutes = (minutes) => {
        if (!Number.isFinite(minutes)) return null;
        if (minutes < 1) return '< 1 min';
        if (minutes < 10) return `${minutes.toFixed(1)} min`;
        return `${Math.round(minutes)} min`;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(`/report-matching/workspaces/${workspaceId}`)}
                    sx={{ px: 0, minWidth: 0 }}
                >
                    Workspace
                </Button>
                <Typography color="text.primary">History</Typography>
            </Breadcrumbs>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                <HistoryIcon color="primary" />
                <Box>
                    <Typography variant="h4" fontWeight={700}>
                        All Available Outputs
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {workspace?.name || 'Workspace'}{workspace?.lineage_filename ? ` | ${workspace.lineage_filename}` : ''}
                    </Typography>
                </Box>
            </Stack>

            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
                <Box sx={{ flex: '1 1 160px' }}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Running</Typography>
                        <Typography variant="h5" fontWeight={800}>{summary.running}</Typography>
                    </Paper>
                </Box>
                <Box sx={{ flex: '1 1 160px' }}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Queued</Typography>
                        <Typography variant="h5" fontWeight={800}>{summary.queued}</Typography>
                    </Paper>
                </Box>
                <Box sx={{ flex: '1 1 160px' }}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Completed</Typography>
                        <Typography variant="h5" fontWeight={800}>{summary.completed}</Typography>
                    </Paper>
                </Box>
                <Box sx={{ flex: '1 1 160px' }}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Failed</Typography>
                        <Typography variant="h5" fontWeight={800}>{summary.failed}</Typography>
                    </Paper>
                </Box>
                <Box sx={{ flex: '1 1 160px' }}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Saved Outputs</Typography>
                        <Typography variant="h5" fontWeight={800}>{summary.outputs}</Typography>
                    </Paper>
                </Box>
            </Stack>

            <Card sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        spacing={2}
                        sx={{ mb: 2.5 }}
                    >
                        <Typography variant="h6" fontWeight={700}>
                            {activeView === 'outputs' ? 'Saved Matching Outputs' : 'Queue History'}
                        </Typography>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1.5}
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                        >
                            {activeView === 'outputs' && (
                                <TextField
                                    size="small"
                                    placeholder="Search output by legacy report"
                                    value={outputSearch}
                                    onChange={(event) => setOutputSearch(event.target.value)}
                                    sx={{ minWidth: { xs: '100%', sm: 260 } }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={activeView}
                                onChange={handleActiveViewChange}
                            >
                                <ToggleButton value="outputs">Outputs</ToggleButton>
                                <ToggleButton value="history">History</ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>
                    </Stack>

                    <Box sx={{ maxHeight: 520, overflowY: 'auto', pr: 0.5 }}>
                        {activeView === 'outputs' ? (
                            filteredSavedMatchings.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    {outputSearch.trim()
                                        ? 'No saved outputs match that report name.'
                                        : 'No saved matching outputs are available yet.'}
                                </Typography>
                            ) : (
                                <Stack spacing={1.5}>
                                    {filteredSavedMatchings.map((item) => (
                                        <Paper key={item.legacy_report_name} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                            <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
                                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                                    {(() => {
                                                        const processingInfo = processingTimeByReport[item.legacy_report_name];
                                                        const processingLabel = formatProcessingMinutes(processingInfo?.minutes);
                                                        return (
                                                            <>
                                                    <Tooltip title={item.legacy_report_name || ''} arrow placement="top-start">
                                                        <Typography variant="subtitle1" fontWeight={700} noWrap>
                                                            {item.legacy_report_name}
                                                        </Typography>
                                                    </Tooltip>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {item.match_count || 0} matches found
                                                    </Typography>
                                                    {processingLabel && (
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            Processed in: {processingLabel}
                                                        </Typography>
                                                    )}
                                                    <Tooltip title={item.best_catalyst_report_name || 'None'} arrow placement="top-start">
                                                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                                            Best match: {item.best_catalyst_report_name || 'None'}
                                                        </Typography>
                                                    </Tooltip>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        Saved: {parseApiDate(item.saved_at)?.toLocaleString() || '-'}
                                                    </Typography>
                                                            </>
                                                        );
                                                    })()}
                                                </Box>
                                                <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<OpenInNewIcon />}
                                                        onClick={() => openSavedOutput(item.legacy_report_name)}
                                                        sx={{ height: 30, minWidth: 76, fontSize: '0.8rem', px: 1.25 }}
                                                    >
                                                        Open
                                                    </Button>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteMatching(item.legacy_report_name)}
                                                        sx={{
                                                            width: 30,
                                                            height: 30,
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            borderRadius: 1,
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </Stack>
                                        </Paper>
                                    ))}
                                </Stack>
                            )
                        ) : matchJobs.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No queued or completed jobs found for this workspace.
                            </Typography>
                        ) : (
                            <List disablePadding>
                                {matchJobs.map((job) => (
                                    <ListItem
                                        key={job.id}
                                        disableGutters
                                        secondaryAction={
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Chip
                                                    size="small"
                                                    label={job.status}
                                                    color={getJobStatusColor(job.status)}
                                                />
                                                {isCancelableJob(job) && (
                                                    <IconButton
                                                        size="small"
                                                        color="warning"
                                                        onClick={() => handleCancelJob(job)}
                                                        disabled={Boolean(stoppingJobIds[job.id])}
                                                    >
                                                        {stoppingJobIds[job.id]
                                                            ? <CircularProgress size={16} color="inherit" />
                                                            : <StopIcon fontSize="small" />}
                                                    </IconButton>
                                                )}
                                                {isDeletableJob(job) && (
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteJob(job)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Stack>
                                        }
                                        sx={{ py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}
                                    >
                                        <ListItemText
                                            primary={(
                                                <Tooltip title={job.legacy_report_name || ''} arrow placement="top-start">
                                                    <Typography fontWeight={600} noWrap sx={{ maxWidth: { xs: 220, sm: 420, md: 520 } }}>
                                                        {job.legacy_report_name}
                                                    </Typography>
                                                </Tooltip>
                                            )}
                                            secondary={`${job.progress_message || 'Waiting for status...'}${getJobReason(job) !== (job.progress_message || 'Waiting for status...') ? `\n${getJobReason(job)}` : ''}`}
                                            secondaryTypographyProps={{ sx: { whiteSpace: 'pre-line' } }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}

export default ReportMatchingHistoryPage;
